import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import { useAppSelector } from 'store/hooks';
import BackgroundTile from 'features/room/settings/tabs/VideoBackgroundTab/BackgroundTile';
import Icon from 'components/Icon';
import webrtcAdapter from 'webrtc-adapter';
import {
  selectCustomVirtualBackground,
  selectVbProviderV2Enabled,
  selectVirtualBackgrounds,
} from 'features/virtual-backgrounds/selectors';
import {
  SelectedVirtualBackground,
  VirtualBackgroundItem,
} from 'features/virtual-backgrounds/types';
import CustomBackgroundUpload from 'features/virtual-backgrounds/components/CustomBackgroundUpload';
import { useTranslation } from 'react-i18next';

type BackgroundGridProps = {
  selected: string;
  onChange: (config: SelectedVirtualBackground) => void;
  onReset: () => void;
  onUpload: (file: File) => void;
  onDelete?: (background: VirtualBackgroundItem) => void;
  isUploading?: boolean;
};

const BackgroundGrid = ({
  selected,
  onChange,
  onReset,
  onUpload,
  isUploading = false,
  onDelete,
}: BackgroundGridProps) => {
  const { t } = useTranslation('settings');

  const backgrounds = useAppSelector(selectVirtualBackgrounds);
  const customBackground = useAppSelector(selectCustomVirtualBackground);

  const vbProviderV2Enabled = useAppSelector(selectVbProviderV2Enabled);

  const { blurBackgrounds, mediaBackgrounds } = useMemo(() => {
    const combined: VirtualBackgroundItem[] = [...backgrounds];

    if (customBackground) {
      combined.push({
        id: `custom-${customBackground.type}`,
        type: customBackground.type === '' ? 'image' : customBackground.type,
        value: customBackground.value,
        thumbnail: customBackground.thumbnail,
        label: 'Custom background',
        isCustom: false, // SDK custom background, not user uploaded
      });
    }

    return {
      blurBackgrounds: combined.filter((bg) => bg.type === 'blur'),
      mediaBackgrounds: combined.filter((bg) => bg.type === 'image' || bg.type === 'video'),
    };
  }, [backgrounds, customBackground]);

  const isSafari = webrtcAdapter.browserDetails.browser === 'safari';

  const blurAvailable = !(isSafari && !vbProviderV2Enabled);

  return (
    <Box
      sx={{
        display: 'grid',
        gap: '8px',
        gridAutoRows: '60px',
        gridTemplateColumns: 'repeat(auto-fit, 88px);',
        justifyContent: 'center',
      }}
    >
      <BackgroundTile selected={selected === ''} onClick={onReset} bordered>
        <Icon name="virtual-bg-no-bg" aria-label={t('virtual_bg.aria_label_no_bg')} />
      </BackgroundTile>

      {blurBackgrounds.map((bg) => {
        const isBalanced = bg.id === 'balanced-blur';
        return (
          <BackgroundTile
            data-testid={`blur-control-${bg.id}`}
            key={bg.id}
            tooltipText={!blurAvailable ? t('virtual_bg.blur_not_supported') : undefined}
            disabled={!blurAvailable}
            selected={selected === bg.value}
            onClick={() => onChange(bg)}
            bordered
          >
            <Icon
              name={isBalanced ? 'virtual-bg-small-blur' : 'virtual-bg-strong-blur'}
              aria-label={t(
                isBalanced
                  ? 'virtual_bg.aria_label_light_blur'
                  : 'virtual_bg.aria_label_strong_blur'
              )}
            />
          </BackgroundTile>
        );
      })}

      <CustomBackgroundUpload onSelect={onUpload} isUploading={isUploading} />

      {mediaBackgrounds.map((bg, i) => (
        <BackgroundTile
          key={bg.id}
          selected={selected === bg.value}
          onClick={() => onChange(bg)}
          onDelete={bg.isCustom ? () => onDelete?.(bg) : undefined}
          isCustom={bg.isCustom}
        >
          <img src={bg.thumbnail || bg.value} alt={bg.label || `Background ${i}`} />
        </BackgroundTile>
      ))}
    </Box>
  );
};

export default BackgroundGrid;
